import { Document } from '@contentful/rich-text-types';
import { ContentStatus, useContentEntry } from '@soluto-home-web/platform-core';
import { Entry } from 'contentful';
import { Button, LoadingOverlay } from 'packages/smart-home-support/components';
import React, { useEffect, useState } from 'react';
import { useTweekValue } from 'react-tweek';
import styled from 'styled-components';
import RichText from '../../../../components/RichText';
import { LinkId } from '../../../../constants';
import { RefundsDescription, RefundsSeparator } from '../../components/CommonComponents';
import NeedHelpButton from '../../components/NeedHelpButton';
import { PageContainer } from '../../styles';
import { getBreakpoint, getUrl } from '../../utils';
import { RefundSectionProps } from '../RefundGuidePage/RefundSection';
import CashPaymentsIcon from './CashPaymentsIcon';
import ServiceCard, { ServiceCardProps } from './ServiceCard';

const breakpoint = getBreakpoint();

const s = {
  PageContainer: styled(PageContainer)`
    max-width: 780px;
    text-align: center;
    padding-bottom: 30px;

    @media ${breakpoint} {
      padding-bottom: 60px;
    }
  `,
  Separator: styled(RefundsSeparator)`
    @media ${breakpoint} {
      display: none;
    }
  `,
  SelectionsWrapper: styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
  `,
  ServicesContainer: styled.div`
    margin: 2em 0;
    display: grid;
    grid-gap: 20px;
    grid-auto-rows: 1fr;
    grid-template-columns: repeat(2, 1fr);

    @media ${breakpoint} {
      grid-template-columns: repeat(4, 1fr);
    }
  `,
  ContinueButton: styled(Button)`
    position: sticky;
    margin: 20px auto 50px auto;
    bottom: 50px;
    z-index: 2;
    width: 280px;
    color: white;
    text-align: center;
    font-weight: bold;
  `,
  BottomSection: styled.section`
    font-size: 18px;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 40px;

    @media ${breakpoint} {
      flex-direction: row;
      align-items: flex-start;
      justify-content: space-evenly;
    }
  `,
  WarrantiesContainer: styled.div`
    text-align: center;
    max-width: 340px;

    @media ${breakpoint} {
      text-align: start;
    }
  `,
  BottomHeader: styled.h1`
    font-size: inherit;
  `,
  List: styled.ul`
    list-style: '- ' inside;
    text-align: start;
    padding: 0;
  `,
  NeedHelpButton: styled(NeedHelpButton)`
    align-self: center;
  `,
};

type RefundsPageContent = {
  selectionDescription: Document;
  contentDescription: Document;
  sections: Entry<RefundSectionProps>[];
};

const RefundSelectionPage = () => {
  const [services, setServices] = useState<Omit<ServiceCardProps, 'onSelection'>[]>();
  const contentId = useTweekValue('soluto_home_web/smart_home_support/pages/refunds/content_id', '');
  const content = useContentEntry<RefundsPageContent>(contentId);

  useEffect(() => {
    if (content.status === ContentStatus.loaded) {
      setServices(content.data.fields.sections.map((i) => ({ selected: false, data: i.fields })));
    } else {
      setServices(undefined);
    }
  }, [content]);

  const selectedServices = services && services.filter((s) => s.selected).map((s) => s.data);

  return (
    <>
      <LoadingOverlay in={content.status === ContentStatus.loading} />
      {content.status === ContentStatus.loaded && (
        <s.PageContainer>
          <CashPaymentsIcon />
          <RefundsDescription>
            <RichText content={content.data.fields.selectionDescription} />
          </RefundsDescription>
          <s.Separator />
          <s.SelectionsWrapper>
            <s.ServicesContainer>
              {services &&
                services.map((service, i) => (
                  <ServiceCard
                    key={i}
                    {...service}
                    onSelection={(selected) => {
                      setServices((services) => {
                        const copy = [...services!];
                        copy[i] = { ...services![i], selected };
                        return copy;
                      });
                    }}
                  />
                ))}
            </s.ServicesContainer>
            <s.ContinueButton
              id={LinkId.RefundSelectionContinue}
              disabled={!selectedServices || selectedServices.length === 0}
              to={getUrl('refund-guide')}
              onClick={() => {
                sessionStorage.setItem('refund-description', JSON.stringify(content.data.fields.contentDescription));
                sessionStorage.setItem('selected-refund-services', JSON.stringify(selectedServices));
              }}
            >
              Continue
            </s.ContinueButton>
          </s.SelectionsWrapper>
          <RefundsSeparator />
          <s.BottomSection>
            <s.WarrantiesContainer>
              <s.BottomHeader>Need help finding your warranties?</s.BottomHeader>
              <s.List>
                <li>Try searching your email for ‘warranty’</li>
                <li>Take a peek at your receipts drawer</li>
              </s.List>
              <p>Or let’s connect you with an expert and figure this out together.</p>
            </s.WarrantiesContainer>
            <s.NeedHelpButton id={LinkId.RefundSelectionNeedHelpCall} text="Need help? Message us." />
          </s.BottomSection>
        </s.PageContainer>
      )}
    </>
  );
};

export default RefundSelectionPage;



// WEBPACK FOOTER //
// ./packages/smart-home-support/pages/profile/subpages/RefundSelectionPage/RefundSelectionPage.tsx